import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBatteryFull, faDownload } from '@fortawesome/free-solid-svg-icons';
import { usePlantData } from '../../../components/data/PlantData';
import SidebarPageLayout from '../../../components/layout/SidebarPageLayout';
import Marquee from '../../../components/layout/Marquee';
import Breadcrumbs from '../../../components/layout/Breadcrumbs';
import DayRangePicker from '../../../components/layout/DayRangePicker';
import UnusualnessBarChart from '../../../components/graphs/UnusualnessBarChart';
import AnomalyWatchlistTable from '../../../components/graphs/AnomalyWatchlistTable';
import {
  downloadAnomalyDetailPdf,
  fetchBessAnomalyCatalog,
  fetchIsolationForestWatchlist,
  saveBlobAsFile,
} from '../../../api/bessAnomaly';
import {
  readIsolationForestCache,
  sliceIsolationForestSeries,
  writeIsolationForestCache,
} from '../../../api/bessAnomalyCache';
import { toSlug } from '../../../util/strings';
import { isMetricEnabled, useSiteMetricFlags } from '../../../utils/siteMetricFlags';

const FALLBACK_SERIES = Object.freeze([
  { series: 'A', deviceCount: 19 },
  { series: 'B', deviceCount: 103 },
  { series: 'D', deviceCount: 46 },
  { series: 'E', deviceCount: 6 },
  { series: 'SB', deviceCount: 1 },
]);

const SERIES_ORDER = Object.freeze(['A', 'B', 'D', 'E', 'SB']);

function seriesOfBattery(name) {
  const raw = String(name || '').trim().toUpperCase();
  if (!raw) return null;
  if (/^SB\d+$/.test(raw)) return 'SB';
  const match = raw.match(/^([A-Z]+)\d+$/);
  if (!match) return null;
  const prefix = match[1];
  return SERIES_ORDER.includes(prefix) ? prefix : prefix;
}

function toLocalIsoDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Rolling previous 1 year through today (inclusive). */
function previousOneYearRange(today = new Date()) {
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
  const start = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
  return { start, end };
}

function AnalysisLoader({ label = 'Loading anomaly analysis…' }) {
  return (
    <div className="text-center py-5" role="status" aria-live="polite">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">{label}</span>
      </div>
      <div className="text-muted small mt-2">{label}</div>
      <div className="text-muted small">Usually a few seconds once the feature mart is warm.</div>
    </div>
  );
}

function Batteries() {
  const { plant: gemini } = usePlantData();
  const metricFlags = useSiteMetricFlags(gemini?.plant_name);
  const batteriesEnabled = isMetricEnabled(metricFlags, 'battery_racks');
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [activePdfKey, setActivePdfKey] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  const defaultRange = useMemo(() => previousOneYearRange(), []);

  const [analysisRange, setAnalysisRange] = useState(defaultRange);
  const [analysisSeries, setAnalysisSeries] = useState('ALL');
  /** Full categorized payload for the current date range (memory + localStorage). */
  const [categorizedPayload, setCategorizedPayload] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  const portfolioName = toSlug(gemini.plant_name) || 'gemini';
  const dateRange = useMemo(() => {
    if (analysisRange?.start && analysisRange?.end) {
      return {
        startDate: toLocalIsoDate(analysisRange.start),
        endDate: toLocalIsoDate(analysisRange.end),
      };
    }
    const fallback = previousOneYearRange();
    return {
      startDate: toLocalIsoDate(fallback.start),
      endDate: toLocalIsoDate(fallback.end),
    };
  }, [analysisRange]);

  const isolationSeriesOptions = useMemo(() => {
    const family = (catalog?.families || []).find((f) => f.id === 'isolation_forest');
    const fromCatalog = (family?.series || []).filter((s) => s.series && s.series !== 'ALL');
    const base = fromCatalog.length ? fromCatalog : FALLBACK_SERIES;
    const allCount = (catalog?.families || [])
      .flatMap((f) => f.series || [])
      .find((s) => s.series === 'ALL')
      ?.deviceCount;
    return [{ series: 'ALL', deviceCount: allCount }, ...base];
  }, [catalog]);

  const watchlistResult = useMemo(
    () => (categorizedPayload
      ? sliceIsolationForestSeries(categorizedPayload, analysisSeries)
      : null),
    [categorizedPayload, analysisSeries],
  );

  const anomalyWatchlist = useMemo(() => {
    const list = watchlistResult?.watchlist || [];
    return list.filter((row) => row?.status === 'ANOMALY');
  }, [watchlistResult]);

  /** All-row plus one download row per series that has red anomalies in the chart. */
  const anomalySeriesRows = useMemo(() => {
    if (!anomalyWatchlist.length) return [];

    const bySeries = new Map();
    for (const row of anomalyWatchlist) {
      const series = seriesOfBattery(row.battery);
      if (!series || !SERIES_ORDER.includes(series)) continue;
      if (!bySeries.has(series)) bySeries.set(series, []);
      bySeries.get(series).push(row.battery);
    }

    const allBatteries = anomalyWatchlist
      .map((row) => row.battery)
      .filter(Boolean);

    const perSeries = SERIES_ORDER
      .filter((series) => bySeries.has(series))
      .map((series) => ({
        series,
        label: series,
        batteries: bySeries.get(series),
        anomalyCount: bySeries.get(series).length,
      }));

    return [
      {
        series: 'ALL',
        label: 'All',
        batteries: allBatteries,
        anomalyCount: allBatteries.length,
      },
      ...perSeries,
    ];
  }, [anomalyWatchlist]);

  const emptySeriesMessage = useMemo(() => {
    if (!watchlistResult) return null;
    if (watchlistResult.emptyMessage) return watchlistResult.emptyMessage;
    if (!watchlistResult.watchlist?.length) {
      return `No data available for series ${analysisSeries === 'ALL' ? 'All' : analysisSeries} in this date range.`;
    }
    if (!anomalyWatchlist.length) {
      return `No batteries flagged as anomalies for series ${analysisSeries === 'ALL' ? 'All' : analysisSeries} in this date range.`;
    }
    return null;
  }, [watchlistResult, anomalyWatchlist.length, analysisSeries]);

  useEffect(() => {
    document.title = 'Vivosol Plant Performance Insights Hub | Gemini / Diagnostics / Batteries';
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);

    (async () => {
      try {
        const data = await fetchBessAnomalyCatalog(portfolioName);
        if (cancelled) return;
        setCatalog(data);
      } catch (err) {
        if (cancelled) return;
        console.warn('BESS anomaly catalog failed; using fallback series counts', err);
        setCatalog(null);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [portfolioName]);

  const loadCategorizedAnalysis = useCallback(async (startDate, endDate, { force = false, signal } = {}) => {
    if (!startDate || !endDate) return;

    if (!force) {
      const cached = readIsolationForestCache(portfolioName, startDate, endDate);
      if (cached?.bySeries) {
        if (signal?.aborted) return;
        setCategorizedPayload(cached);
        setAnalysisError(null);
        setAnalysisLoading(false);
        return;
      }
    }

    setAnalysisLoading(true);
    setAnalysisError(null);
    try {
      // Always fetch ALL; backend returns bySeries for instant dropdown switches.
      const data = await fetchIsolationForestWatchlist(portfolioName, {
        series: 'ALL',
        startDate,
        endDate,
      });
      if (signal?.aborted) return;
      if (!data?.bySeries) {
        throw new Error('Analysis response missing bySeries categorization');
      }
      writeIsolationForestCache(portfolioName, startDate, endDate, data);
      setCategorizedPayload(data);
    } catch (err) {
      if (signal?.aborted) return;
      setCategorizedPayload(null);
      setAnalysisError(err?.message || 'Analysis failed');
    } finally {
      if (!signal?.aborted) setAnalysisLoading(false);
    }
  }, [portfolioName]);

  // Fetch (or hydrate from localStorage) when date range changes — not on series change.
  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) return undefined;
    const controller = { aborted: false };
    loadCategorizedAnalysis(dateRange.startDate, dateRange.endDate, { signal: controller });
    return () => {
      controller.aborted = true;
    };
  }, [portfolioName, dateRange.startDate, dateRange.endDate, loadCategorizedAnalysis]);

  const handleRunAnalysis = () => {
    loadCategorizedAnalysis(dateRange.startDate, dateRange.endDate, { force: true });
  };

  const handleDownloadDetailPdf = async (series, batteries) => {
    const key = series;
    setActivePdfKey(key);
    setDownloadError(null);

    try {
      const { blob, fileName } = await downloadAnomalyDetailPdf(portfolioName, {
        series,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        batteries,
      });
      saveBlobAsFile(blob, fileName);
    } catch (err) {
      setDownloadError(err?.message || 'PDF download failed');
    } finally {
      setActivePdfKey(null);
    }
  };

  if (metricFlags && !batteriesEnabled) {
    return (
      <SidebarPageLayout>
        <div className="content p-5">
          <h1 className="mt-3 mb-0">Batteries</h1>
          <p>Battery diagnostics are turned off for this plant.</p>
        </div>
      </SidebarPageLayout>
    );
  }

  return (
    <SidebarPageLayout>
      <div className="content p-5">
        <Marquee />
        <h1 className="mt-3 mb-0">
          <Link to={`${gemini.dashboard || '#'}`}>
            <strong>{gemini.plant_name || 'Plant'}</strong>
          </Link>
          {' '}
          : {gemini.config} ({gemini.location})
        </h1>
        <Breadcrumbs labels={{ portfolioName: gemini.plant_name }} />

        <div className="d-flex align-items-baseline justify-content-between gap-3 mb-3 flex-wrap">
          <h4 className="mt-0 mb-0">
            <FontAwesomeIcon icon={faBatteryFull} style={{ transform: 'rotate(-90deg)' }} />
            {' '}
            Diagnostics — Batteries
          </h4>
          <span className="text-muted small">
            Analysis window: {dateRange.startDate} → {dateRange.endDate}
            {catalogLoading ? ' · Loading catalog…' : null}
            {analysisLoading ? ' · Analyzing…' : null}
          </span>
        </div>

        <p className="text-muted mb-4" style={{ maxWidth: '52rem' }}>
          Isolation Forest ranking from pre-aggregated Snowflake cell-temperature features
          (dbt mart; falls back to live RAW if the mart is empty). Defaults to all series
          over the previous year; pick any date range within the last two years. Series
          switches use a cached categorized result (no re-query). Download per-series
          detail PDFs for flagged anomaly batteries below.
        </p>

        <div className="d-flex flex-wrap align-items-end gap-3 mb-4">
          <label className="mb-0">
            <span className="form-label small text-muted mb-1 d-block">Series</span>
            <select
              className="form-select form-select-sm"
              style={{ minWidth: '7rem' }}
              value={analysisSeries}
              onChange={(e) => setAnalysisSeries(e.target.value)}
              disabled={analysisLoading && !categorizedPayload}
            >
              {isolationSeriesOptions.map((entry) => (
                <option key={entry.series} value={entry.series}>
                  {entry.series === 'ALL' ? 'All' : entry.series}
                  {entry.deviceCount != null ? ` (${entry.deviceCount})` : ''}
                </option>
              ))}
            </select>
          </label>

          <div>
            <span className="form-label small text-muted mb-1 d-block">Date range</span>
            <DayRangePicker
              range={analysisRange}
              setRange={setAnalysisRange}
              maxRangeDays={null}
            />
          </div>

          <div className="bess-download-actions">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={analysisLoading || !dateRange.startDate || !dateRange.endDate}
              onClick={handleRunAnalysis}
            >
              {analysisLoading ? 'Analyzing…' : 'Refresh analysis'}
            </button>
          </div>
        </div>

        <div className="panel p-3 mb-4" id="bess-isolation-forest-live">
          <div className="header d-flex justify-content-between pb-3 mb-3 flex-wrap gap-2">
            <div className="left">
              <span>Cell temperature unusualness</span>
              <div className="text-muted small mt-1">
                Flagged anomaly batteries only (red).
              </div>
            </div>
          </div>

          {analysisError ? (
            <div className="alert alert-danger py-2" role="alert">{analysisError}</div>
          ) : null}

          {analysisLoading ? (
            <AnalysisLoader />
          ) : watchlistResult ? (
            emptySeriesMessage && !anomalyWatchlist.length ? (
              <div className="alert alert-secondary py-2 mb-0" role="status">
                {emptySeriesMessage}
              </div>
            ) : (
              <>
                <UnusualnessBarChart
                  watchlist={anomalyWatchlist}
                  series={watchlistResult.series || analysisSeries}
                />
                <p className="text-muted small mt-2 mb-0">
                  {watchlistResult.anomalyCount}
                  {' / '}
                  {watchlistResult.deviceCount}
                  {' flagged · source '}
                  {watchlistResult.source || 'snowflake'}
                  {' · '}
                  {watchlistResult.startDate}
                  {' → '}
                  {watchlistResult.endDate}
                </p>
              </>
            )
          ) : (
            <p className="text-muted small mb-0">
              Select a date range to load anomaly analysis.
            </p>
          )}
        </div>

        <div className="panel p-3 mb-4" id="bess-isolation-forest-anomalies">
          <div className="header d-flex justify-content-between pb-3 mb-3 flex-wrap gap-2">
            <div className="left">
              <span>Anomaly zone batteries</span>
              <div className="text-muted small mt-1">
                Detail rows for devices flagged ANOMALY (red) in the chart above.
              </div>
            </div>
          </div>

          {analysisLoading ? (
            <AnalysisLoader label="Loading anomaly watchlist…" />
          ) : watchlistResult ? (
            emptySeriesMessage && !anomalyWatchlist.length ? (
              <div className="alert alert-secondary py-2 mb-0" role="status">
                {emptySeriesMessage}
              </div>
            ) : (
              <AnomalyWatchlistTable
                watchlist={watchlistResult.watchlist}
                series={watchlistResult.series || analysisSeries}
              />
            )
          ) : (
            <p className="text-muted small mb-0">
              Anomaly rows appear here after analysis completes.
            </p>
          )}
        </div>

        {downloadError ? (
          <div className="alert alert-danger py-2" role="alert">{downloadError}</div>
        ) : null}

        <div className="panel p-3 mb-4" id="bess-anomaly-detail-pdfs">
          <div className="header d-flex justify-content-between pb-3 mb-3">
            <div className="left">
              <span>Anomaly detail reports</span>
              <div className="text-muted small mt-1">
                One PDF per series with anomalies in the chart above. Each report includes
                Per-Device Detail Plots for flagged batteries, plus the rule-based
                BESS Anomaly Report summary table (red-flag cells) for that series.
              </div>
            </div>
          </div>

          {analysisLoading ? (
            <AnalysisLoader label="Loading anomaly series…" />
          ) : anomalySeriesRows.length === 0 ? (
            <p className="text-muted small mb-0">
              {watchlistResult
                ? 'No anomaly series to download for the current chart selection.'
                : 'Detail report downloads appear here after analysis finds anomalies.'}
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0 bess-anomaly-table">
                <thead>
                  <tr>
                    <th scope="col" className="bess-anomaly-table__series">Series</th>
                    <th scope="col" className="bess-anomaly-table__devices">Anomalies</th>
                    <th scope="col" className="bess-anomaly-table__downloads">Downloads</th>
                  </tr>
                </thead>
                <tbody>
                  {anomalySeriesRows.map((entry) => {
                    const busy = activePdfKey === entry.series;
                    const disabled = Boolean(activePdfKey);
                    return (
                      <tr key={entry.series}>
                        <td className="bess-anomaly-table__series">
                          <strong>{entry.label || entry.series}</strong>
                          {entry.series === 'ALL' ? (
                            <span className="text-muted small ms-2">site-wide rollup</span>
                          ) : null}
                        </td>
                        <td className="bess-anomaly-table__devices">{entry.anomalyCount}</td>
                        <td className="bess-anomaly-table__downloads">
                          <div className="bess-download-actions">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              disabled={disabled}
                              onClick={() => handleDownloadDetailPdf(entry.series, entry.batteries)}
                            >
                              <FontAwesomeIcon icon={faDownload} className="me-1" />
                              {busy ? 'Generating…' : 'Download PDF'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-muted small mb-0">
          Isolation Forest results are a ranked watchlist for inspection, not a field diagnosis.
          Confirm flags before acting.
        </p>
      </div>
    </SidebarPageLayout>
  );
}

export default Batteries;
